import { Controller } from "@hotwired/stimulus";

// Runs inside a filter chip's dropdown panel: search filtering, multi/single
// selection, keyboard navigation, autofocus on open, and (multi-select only)
// hoist-on-close that pins selected rows to a synthetic "selected" section.
export default class extends Controller {
  static targets = [
    "searchInput",
    "searchClear",
    "item",
    "empty",
    "section",
    "selectedSection",
    "divider",
    "sections",
  ];
  static values = {
    selectionMode: { type: String, default: "multi" },
    // Single-select panels refuse to deselect the current row. Used by the
    // 3-segment chip's operator panel (exactly-one contract).
    selectionRequired: { type: Boolean, default: false },
  };

  focusSearchIfOwner(event) {
    if (!event.target || !event.target.contains(this.element)) return;
    if (!this.hasSearchInputTarget) return;
    // The dropdown's menu element. Used in the deferred callback to make
    // sure we don't focus into a panel that closed inside this same frame.
    const ownerMenu = event.target.querySelector("[data-dropdown-target='menu']");
    // Defer one frame so Floating UI positioning settles before focus.
    requestAnimationFrame(() => {
      if (!this.hasSearchInputTarget) return;
      // Bail if the dropdown was closed (or the panel removed) between
      // dropdown:shown and this frame; otherwise we'd focus a hidden input.
      if (ownerMenu && ownerMenu.classList.contains("hidden")) return;
      if (!this.searchInputTarget.isConnected) return;
      this.searchInputTarget.focus();
    });
  }

  search() {
    const query = (this.searchInputTarget.value || "").trim().toLowerCase();
    let totalVisible = 0;

    this.itemTargets.forEach((item) => {
      const name = (item.dataset.name || "").toLowerCase();
      const matches = query === "" || name.includes(query);
      item.hidden = !matches;
      if (matches) totalVisible += 1;
    });

    // Divider i stays only if sections[i] and sections[i+1] both have a
    // visible item.
    const sectionVisibility = this.sectionTargets.map((section) => {
      const itemsInSection = section.querySelectorAll(
        "[data-filter-option-list-target='item']"
      );
      const anyVisible = Array.from(itemsInSection).some((item) => !item.hidden);
      section.hidden = !anyVisible;
      return anyVisible;
    });

    this.dividerTargets.forEach((divider, i) => {
      divider.hidden = !(sectionVisibility[i] && sectionVisibility[i + 1]);
    });

    if (this.hasEmptyTarget) {
      this.emptyTarget.hidden = totalVisible > 0;
    }

    if (this.hasSearchClearTarget) {
      this.searchClearTarget.hidden = this.searchInputTarget.value === "";
    }

    this._resetRovingTabindex();
  }

  clearSearch(event) {
    if (event && event.type === "dropdown:hidden") {
      if (!event.target || !event.target.contains(this.element)) return;
    }
    if (!this.hasSearchInputTarget) return;
    if (this.searchInputTarget.value === "") return;

    this.searchInputTarget.value = "";
    this.search();

    if (event && event.type === "click") {
      this.searchInputTarget.focus();
    }
  }

  toggle() {
    this.dispatch("change", {
      detail: { selected: this.selectedValues },
      bubbles: true,
    });
  }

  // Rewrites every row so a server-rendered multi-selected state self-heals.
  // With selectionRequired, re-clicking the current row is a no-op.
  selectSingle(event) {
    const row = event.currentTarget;
    const wasSelected = row.getAttribute("aria-selected") === "true";
    if (this.selectionRequiredValue && wasSelected) return;

    const nextSelected = wasSelected ? null : row;

    this.singleRows.forEach((r) => {
      r.setAttribute("aria-selected", r === nextSelected ? "true" : "false");
    });

    this.dispatch("change", {
      detail: { selected: this.selectedValues },
      bubbles: true,
    });
  }

  keydown(event) {
    const target = event.target;
    const onSearch = this.hasSearchInputTarget && target === this.searchInputTarget;
    const onFocusable = this._focusableRows.includes(target);

    if (!onSearch && !onFocusable) return;

    if (onSearch && event.key === "ArrowDown") {
      const first = this._visibleFocusableRows[0];
      if (first) {
        event.preventDefault();
        this._focusAndRove(first);
      }
      return;
    }

    if (!onFocusable) return;

    switch (event.key) {
      case "ArrowDown": {
        event.preventDefault();
        const visible = this._visibleFocusableRows;
        const idx = visible.indexOf(target);
        // No wrap: ArrowDown on the last row is a no-op.
        if (idx >= 0 && idx < visible.length - 1) {
          this._focusAndRove(visible[idx + 1]);
        }
        break;
      }
      case "ArrowUp": {
        event.preventDefault();
        const visible = this._visibleFocusableRows;
        const idx = visible.indexOf(target);
        if (idx === 0) {
          if (this.hasSearchInputTarget) this.searchInputTarget.focus();
        } else if (idx > 0) {
          this._focusAndRove(visible[idx - 1]);
        }
        break;
      }
      case "Home": {
        event.preventDefault();
        const first = this._visibleFocusableRows[0];
        if (first) this._focusAndRove(first);
        break;
      }
      case "End": {
        event.preventDefault();
        const visible = this._visibleFocusableRows;
        const last = visible[visible.length - 1];
        if (last) this._focusAndRove(last);
        break;
      }
      case "Enter":
      case " ": {
        const isEnter = event.key === "Enter";
        // Space toggles checkboxes natively; only intercept it for
        // single-select rows. Enter is intercepted in both modes.
        if (this.selectionModeValue === "single") {
          event.preventDefault();
          target.click();
        } else if (isEnter) {
          event.preventDefault();
          this._applyEnterSelection(target);
        }
        // Enter commits and closes the panel in both modes; Space only toggles.
        if (isEnter) this._requestClose();
        break;
      }
      default:
        break;
    }
  }

  hoistOnClose(event) {
    if (!event.target || !event.target.contains(this.element)) return;
    this.rehome();
  }

  resetIfOwner(event) {
    if (!event.target || !event.target.contains(this.element)) return;
    this.reset();
  }

  // Returns the panel to its pristine state: no query, no hoist. Called
  // when the chip is dismissed so the next open shows everything in order.
  reset() {
    if (this.hasSearchInputTarget && this.searchInputTarget.value !== "") {
      this.searchInputTarget.value = "";
      this.search();
    }
    this.rehome();
  }

  // Rebuilds sections to match what the server would render for the current
  // checkbox state. No-op for single-select panels.
  rehome() {
    if (this.selectionModeValue !== "multi") return;
    if (!this.hasSectionsTarget) return;

    const allItems = Array.from(this.itemTargets);
    const pinnedItems = allItems.filter((li) => li.dataset.pinned === "true");
    const regularItems = allItems.filter((li) => li.dataset.pinned !== "true");

    // Idempotency: invariant is "synthetic selected section exists iff
    // (non-pinned) items are checked". When neither is true the panel is
    // already pristine, so skipping avoids an O(n log n) rebuild on
    // potentially huge option lists. The pinned section is static, so a
    // checked pinned item never forces a rebuild on its own.
    if (
      !this.hasSelectedSectionTarget &&
      !regularItems.some((li) => {
        const cb = li.querySelector("input[type='checkbox']");
        return cb && cb.checked;
      })
    ) {
      return;
    }

    const container = this.sectionsTarget;

    // Mirror the server's case-insensitive abc sort within each section.
    const byLabel = (a, b) =>
      (a.dataset.name || "").toLowerCase().localeCompare(
        (b.dataset.name || "").toLowerCase(),
      );

    const checkedInOrder = regularItems
      .filter((li) => {
        const cb = li.querySelector("input[type='checkbox']");
        return cb && cb.checked;
      })
      .sort(byLabel);

    const uncheckedByHome = new Map();
    regularItems.forEach((li) => {
      const cb = li.querySelector("input[type='checkbox']");
      if (!cb || cb.checked) return;
      const idx = Number(li.getAttribute("data-home-section-index"));
      if (!Number.isFinite(idx) || idx < 0) return;
      if (!uncheckedByHome.has(idx)) uncheckedByHome.set(idx, []);
      uncheckedByHome.get(idx).push(li);
    });
    uncheckedByHome.forEach((items) => items.sort(byLabel));

    // Items survive in memory; Stimulus rebinds targets/actions on re-append.
    container.replaceChildren();

    let needsLeadingDivider = false;

    // Pinned section always leads, in its server-rendered (caller) order.
    if (pinnedItems.length > 0) {
      const pinned = this._createSection({ kind: "pinned", index: -2 });
      pinnedItems.forEach((li) => pinned.appendChild(li));
      container.appendChild(pinned);
      needsLeadingDivider = true;
    }

    if (checkedInOrder.length > 0) {
      if (needsLeadingDivider) container.appendChild(this._createDivider());
      const selected = this._createSection({ kind: "selected", index: -1 });
      checkedInOrder.forEach((li) => selected.appendChild(li));
      container.appendChild(selected);
      needsLeadingDivider = true;
    }

    Array.from(uncheckedByHome.keys())
      .sort((a, b) => a - b)
      .forEach((homeIndex) => {
        const items = uncheckedByHome.get(homeIndex);
        if (!items || items.length === 0) return;
        if (needsLeadingDivider) container.appendChild(this._createDivider());
        const section = this._createSection({ kind: "home", index: homeIndex });
        items.forEach((li) => section.appendChild(li));
        container.appendChild(section);
        needsLeadingDivider = true;
      });

    this._resetRovingTabindex();
  }

  get checkboxes() {
    return this.itemTargets
      .map((item) => item.querySelector("input[type='checkbox']"))
      .filter((cb) => cb !== null);
  }

  get singleRows() {
    return this.itemTargets.filter((item) => item.getAttribute("role") === "option");
  }

  get selectedValues() {
    if (this.selectionModeValue === "single") {
      return this.singleRows
        .filter((r) => r.getAttribute("aria-selected") === "true")
        .map((r) => r.dataset.value);
    }

    return this.checkboxes.filter((cb) => cb.checked).map((cb) => cb.value);
  }

  // ─── private helpers ──────────────────────────────────────────────────────

  // Asks the enclosing dropdown to close (Enter = commit + close). Bubbles to
  // an element-scoped action on the chip/segment, so siblings are unaffected.
  _requestClose() {
    this.dispatch("close", { bubbles: true });
  }

  // Enter flips the focused row against its applied baseline (defaultChecked),
  // not its live state, so a space/click selection survives. change fires only
  // when the state actually moves.
  _applyEnterSelection(checkbox) {
    const desired = !checkbox.defaultChecked;
    if (checkbox.checked === desired) return;

    checkbox.checked = desired;
    checkbox.dispatchEvent(new Event("change", { bubbles: true }));
  }

  // Single-select focuses on the <li>; multi-select focuses on the checkbox.
  get _focusableRows() {
    if (this.selectionModeValue === "single") {
      return this.singleRows;
    }
    return this.checkboxes;
  }

  get _visibleFocusableRows() {
    return this._focusableRows.filter((el) => {
      const item = el.closest("[data-filter-option-list-target='item']") || el;
      return !item.hidden;
    });
  }

  _focusAndRove(el) {
    this._focusableRows.forEach((r) => {
      r.setAttribute("tabindex", r === el ? "0" : "-1");
    });
    el.focus();
  }

  // Moves the single tab stop to a visible row after filter/reshuffle.
  _resetRovingTabindex() {
    const rows = this._focusableRows;
    if (rows.length === 0) return;

    const currentStop = rows.find((r) => r.getAttribute("tabindex") === "0");
    const currentStopVisible = currentStop && !(
      (currentStop.closest("[data-filter-option-list-target='item']") || currentStop).hidden
    );
    if (currentStopVisible) return;

    const nextStop = this._visibleFocusableRows[0] || rows[0];
    rows.forEach((r) => r.setAttribute("tabindex", r === nextStop ? "0" : "-1"));
  }

  _createSection({ kind, index }) {
    const ul = document.createElement("ul");
    ul.className = "filter-option-list__section";
    const targets = kind === "selected" ? "section selectedSection" : "section";
    ul.setAttribute("data-filter-option-list-target", targets);
    ul.setAttribute("data-section-kind", kind);
    ul.setAttribute("data-section-index", String(index));
    return ul;
  }

  _createDivider() {
    const d = document.createElement("div");
    d.className = "filter-option-list__divider";
    d.setAttribute("data-filter-option-list-target", "divider");
    d.setAttribute("role", "presentation");
    return d;
  }
}
